/** Tab E — Customer Experience report. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, Legend,
} from "recharts";
import { useDrillThrough } from "@/hooks/useDrillThrough";
import { COLORS } from "@/components/reports/reportTheme";
import { Heart, AlertTriangle, Languages, Star } from "lucide-react";

// =============== Tab E — Customer Experience ===============
export function CxReport({ filters }) {
  const [data, setData] = useState(null);
  const drillTo = useDrillThrough();
  useEffect(() => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => (params[k] === "all" || params[k] === "") && delete params[k]);
    api.get("/analytics/cx", { params }).then((r) => setData(r.data));
  }, [JSON.stringify(filters)]); // eslint-disable-line

  if (!data) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="cx-report">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reopen rate</div>
              <Heart className="w-4 h-4 text-rose-600" />
            </div>
            <div className={`font-heading text-2xl font-bold mt-1 ${data.kpi.reopen_rate_pct > 10 ? "text-rose-700" : "text-emerald-700"}`}>
              {data.kpi.reopen_rate_pct}%
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{data.kpi.total_reopened} of {data.kpi.total_resolved} resolved</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">First-contact resolution</div>
              <Heart className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-emerald-700">{data.kpi.fcr_pct}%</div>
            <div className="text-[11px] text-slate-500 mt-1">resolved without dept transfer</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pain customers</div>
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="font-heading text-2xl font-bold mt-1 text-amber-700">{data.top_pain_customers.length}</div>
            <div className="text-[11px] text-slate-500 mt-1">≥3 tickets in window</div>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 ${data.csat?.available ? "" : "bg-slate-50/50"}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">CSAT</div>
              <Heart className={`w-4 h-4 ${data.csat?.available ? "text-rose-500" : "text-slate-400"}`} />
            </div>
            {data.csat?.available ? (
              <>
                <div className="font-heading text-2xl font-bold mt-1 text-rose-700">{data.csat.avg_score.toFixed(2)} <span className="text-sm text-slate-400 font-normal">/ 5</span></div>
                <div className="text-[11px] text-slate-500 mt-1">{data.csat.response_rate_pct}% response rate · {data.csat.submitted_count}/{data.csat.sent_count}</div>
              </>
            ) : (
              <>
                <div className="font-heading text-2xl font-bold mt-1 text-slate-400">—</div>
                <div className="text-[10px] text-slate-500 mt-1 italic">No CSAT responses yet</div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="reopen-per-dept-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Reopen rate by department</CardTitle></CardHeader>
          <CardContent className="h-64">
            {data.reopen_per_dept.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No reopens recorded</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={data.reopen_per_dept} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} unit="%" />
                  <YAxis dataKey="dept" type="category" tick={{ fontSize: 10, fill: "#64748b" }} width={120} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, n) => n === "pct" ? `${v}%` : v} />
                  <Bar dataKey="pct" fill="#e11d48" radius={[0, 6, 6, 0]} name="Reopen %" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="language-mix-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-1.5">
              <Languages className="w-4 h-4 text-indigo-600" />Customer language mix
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {data.language_mix.length === 0 ? (
              <div className="text-xs text-slate-400 py-8 text-center">No language data</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.language_mix} dataKey="value" nameKey="name" outerRadius={80} innerRadius={40} paddingAngle={2}>
                    {data.language_mix.map((c, i) => (<Cell key={c.name || `l-${i}`} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="channel-segment-card">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Channel preference: new vs repeat customers</CardTitle></CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer>
            <BarChart data={data.channel_per_segment}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="channel" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="new_customers" fill="#059669" name="New customers" radius={[6, 6, 0, 0]} />
              <Bar dataKey="repeat_customers" fill="#4f46e5" name="Repeat customers" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="top-pain-customers-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-500" />Pain map · top 20 customers (≥3 tickets)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Tickets</TableHead>
                <TableHead>Dominant issue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.top_pain_customers.map((c) => (
                <TableRow key={c.customer_key} className="hover:bg-slate-50" data-testid={`pain-row-${c.customer_key}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{c.name}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{c.contact}</TableCell>
                  <TableCell className="text-right text-sm">
                    <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">{c.ticket_count}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{c.dominant_issue}</TableCell>
                </TableRow>
              ))}
              {data.top_pain_customers.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-400 py-6">No repeat customers in this window — good news.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data.csat?.available && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="csat-panel">
          <Card className="border-slate-200 lg:col-span-1">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base flex items-center gap-1.5"><Heart className="w-4 h-4 text-rose-500" />CSAT score distribution</CardTitle></CardHeader>
            <CardContent className="h-56">
              <ResponsiveContainer>
                <BarChart data={data.csat.distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="score" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#f43f5e" radius={[6, 6, 0, 0]} name="Responses" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-slate-200 lg:col-span-1">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Avg CSAT by department</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-56 overflow-y-auto">
              {(data.csat.by_department || []).length === 0 ? (
                <div className="text-xs text-slate-400 py-8 text-center">No per-dept data yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                      <TableHead>Department</TableHead>
                      <TableHead className="text-right">Avg</TableHead>
                      <TableHead className="text-right">Responses</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.csat.by_department.map((d) => (
                      <TableRow key={d.dept} className="cursor-pointer hover:bg-rose-50/40"
                                onClick={() => drillTo("department_name", d.dept, { date_from: filters.date_from, date_to: filters.date_to })}>
                        <TableCell className="text-sm text-slate-700">{d.dept}</TableCell>
                        <TableCell className="text-right text-sm font-semibold text-rose-700">{d.avg_score.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-xs text-slate-500">{d.submitted}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 lg:col-span-1">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Recent comments</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-56 overflow-y-auto">
              {(data.csat.recent_comments || []).length === 0 ? (
                <div className="text-xs text-slate-400 py-8 text-center">No comments yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.csat.recent_comments.map((c, idx) => (
                    <button key={idx} type="button"
                            onClick={() => drillTo("q", c.ticket_code)}
                            className="w-full text-left px-4 py-2.5 text-xs hover:bg-rose-50/40 transition-colors cursor-pointer"
                            data-testid={`csat-comment-${idx}`}
                            title="Click to open the ticket">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] text-slate-500">{c.ticket_code}</span>
                        <span className="flex items-center gap-0.5">
                          {[1,2,3,4,5].map((n) => (
                            <Star key={n} className={`w-3 h-3 ${n <= c.score ? "fill-amber-500 text-amber-500" : "text-slate-200"}`} />
                          ))}
                        </span>
                      </div>
                      <p className="text-slate-700 mt-1 leading-snug">"{c.comment}"</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

